import React, { useState, useRef, useEffect } from 'react';
import { picture, FULL_WIDTHS, FULL_SIZES } from '../utils/imageUrl';
import { MessageCircle, MoveHorizontal, CheckCircle2 } from 'lucide-react';

export default function BeforeAfterViewer({ works, selectedWork }) {
  const transformWorks = works.filter(w => w.hasBeforeAfter && w.beforeImage);
  const [activeItem, setActiveItem] = useState(null);
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [stageWidth, setStageWidth] = useState(0);
  const containerRef = useRef(null);

  // `works` arrives from storage in an effect, so the first render always sees
  // an empty array. Seeding this from useState meant activeItem latched to null
  // and never recovered — useState's initial value is ignored on re-render — so
  // the whole section returned null even once the data was there.
  const availableIds = transformWorks.map(w => w.id).join(',');

  useEffect(() => {
    if (selectedWork && selectedWork.hasBeforeAfter && selectedWork.beforeImage) {
      setActiveItem(selectedWork);
      setSliderPos(50);
      return;
    }
    setActiveItem((current) => {
      // Keep the current pick if it still exists, otherwise adopt the first.
      if (current && transformWorks.some(w => w.id === current.id)) return current;
      return transformWorks[0] || null;
    });
  }, [selectedWork, availableIds]);

  // The clipped "before" layer needs the stage width to stay un-squashed.
  // Reading containerRef during render gave null on mount, so it rendered
  // compressed until an unrelated re-render happened to fix it.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const sync = () => setStageWidth(el.clientWidth);
    sync();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeItem]);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setSliderPos(percent);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleMove(e.clientX);
  };

  // The pointer almost always leaves the container before the button comes up,
  // so release has to be observed on the window. Bound only while dragging.
  useEffect(() => {
    if (!isDragging) return undefined;

    const stop = () => setIsDragging(false);
    const move = (e) => handleMove(e.clientX);
    const touchMove = (e) => {
      if (e.touches && e.touches[0]) handleMove(e.touches[0].clientX);
    };

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchmove', touchMove, { passive: true });
    window.addEventListener('touchend', stop);
    window.addEventListener('touchcancel', stop);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchmove', touchMove);
      window.removeEventListener('touchend', stop);
      window.removeEventListener('touchcancel', stop);
    };
  }, [isDragging]);

  const handleTouchStart = (e) => {
    setIsDragging(true);
    if (e.touches && e.touches[0]) handleMove(e.touches[0].clientX);
  };

  // Keyboard: the comparison is the whole point of this section, so it cannot
  // be mouse-only. Arrows nudge, shift-arrows jump, Home/End slam to an edge.
  const handleKeyDown = (e) => {
    const step = e.shiftKey ? 10 : 2;
    let next = null;
    if (e.key === 'ArrowLeft') next = sliderPos - step;
    else if (e.key === 'ArrowRight') next = sliderPos + step;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = 100;
    if (next === null) return;
    e.preventDefault();
    setSliderPos(Math.max(0, Math.min(100, next)));
  };

  if (!activeItem) return null;

  const handleWhatsApp = () => {
    const phone = '919747901029';
    const msg = encodeURIComponent(`Hi Handmade bee, I saw the before and after for "${activeItem.title}". I'd love to request a similar custom transformation!`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
  };

  return (
    <section id="transformations" className="py-16 sm:py-24 bg-[#FFF8F2] border-t border-b border-[#4E2C23]/10 relative overflow-hidden">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Centred here because the comparison below it is symmetrical — but
            without the eyebrow pill, so it does not repeat the showcase. */}
        <div className="text-center max-w-xl mx-auto mb-10 sm:mb-12 space-y-3">
          <h2 className="text-3xl sm:text-4xl lg:text-[44px] font-90s font-black text-[#4E2C23] tracking-tight leading-[1.05]">
            Before and after
          </h2>
          <p className="text-sm text-[#745147] leading-relaxed flex items-center justify-center gap-2">
            <MoveHorizontal className="w-4 h-4 text-[#E2725B] flex-shrink-0" aria-hidden="true" />
            <span>Drag the handle, or use the arrow keys, to compare.</span>
          </p>
        </div>

        {/* Work Selection Pills */}
        <div className="flex items-center justify-center gap-2 flex-wrap mb-8">
          {transformWorks.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveItem(item); setSliderPos(50); }}
              className={`px-4 py-2.5 rounded-full text-xs font-bold transition-all border touch-manipulation ${
                activeItem.id === item.id
                  ? 'bg-[#4E2C23] text-[#FFF7F0] border-[#4E2C23] shadow-md scale-105'
                  : 'bg-[#FFFBF7] text-[#4E2C23] border-[#4E2C23]/15 hover:border-[#E2725B]'
              }`}
            >
              {item.title.split(' ')[0]} {item.title.split(' ')[1]} ({item.category})
            </button>
          ))}
        </div>

        {/* Interactive Comparison Split Stage */}
        <div className="max-w-4xl mx-auto">
          <div
            ref={containerRef}
            role="slider"
            tabIndex={0}
            aria-label={`Compare before and after for ${activeItem.title}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(sliderPos)}
            aria-valuetext={`${Math.round(sliderPos)}% before, ${100 - Math.round(sliderPos)}% after`}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onKeyDown={handleKeyDown}
            className="relative h-[360px] sm:h-[480px] rounded-3xl overflow-hidden shadow-xl border-2 border-[#4E2C23]/20 select-none cursor-ew-resize bg-[#FAF2EB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#E2725B]"
          >
            {/* After Image (Full Base) */}
            <img
              src={picture(activeItem, 'image', FULL_WIDTHS, 1024).src}
              srcSet={picture(activeItem, 'image', FULL_WIDTHS, 1024).srcSet}
              sizes={FULL_SIZES}
              alt="After Handcrafted Completion"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
            <div className="absolute bottom-4 right-4 z-20 px-3.5 py-1.5 rounded-full bg-[#FFFBF7]/90 backdrop-blur-md border border-[#4E2C23]/20 text-xs font-bold text-[#4E2C23] flex items-center gap-1.5 shadow-md">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              <span>After</span>
            </div>

            {/* Before Image (Clipped Left Side) */}
            <div
              className="absolute inset-y-0 left-0 overflow-hidden pointer-events-none border-r-2 border-[#E2725B]"
              style={{ width: `${sliderPos}%` }}
            >
              <img
                src={picture(activeItem, 'beforeImage', FULL_WIDTHS, 1024).src}
                srcSet={picture(activeItem, 'beforeImage', FULL_WIDTHS, 1024).srcSet}
                sizes={FULL_SIZES}
                decoding="async"
                alt="Before Initial Stage / Raw Material"
                className="absolute top-0 left-0 h-full object-cover"
                style={{
                  width: stageWidth ? `${stageWidth}px` : '100%',
                  maxWidth: 'none',
                }}
              />
              <div className="absolute bottom-4 left-4 z-20 px-3.5 py-1.5 rounded-full bg-[#4E2C23]/90 backdrop-blur-md border border-white/20 text-xs font-bold text-white flex items-center gap-1.5 shadow-md">
                <span className="w-2 h-2 rounded-full bg-[#FFDAB9]" />
                <span>Before</span>
              </div>
            </div>

            {/* Draggable Divider Handle */}
            <div
              className="absolute top-0 bottom-0 z-30 flex items-center justify-center pointer-events-none"
              style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-1 h-full bg-[#E2725B] shadow-md" />
              <div className="w-10 h-10 rounded-full bg-[#E2725B] border-2 border-white flex items-center justify-center text-white shadow-xl scale-100 hover:scale-110 transition-transform">
                <MoveHorizontal className="w-5 h-5" />
              </div>
            </div>

          </div>

          {/* Details Bar Below Viewer */}
          <div className="mt-6 p-6 rounded-2xl bg-[#FFFBF7] border border-[#4E2C23]/15 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase font-bold text-[#B84D37]">Selected Transformation</span>
              </div>
              <h3 className="font-90s text-lg font-bold text-[#4E2C23] mt-0.5">{activeItem.title}</h3>
              <p className="text-xs text-[#745147] mt-1 max-w-xl">{activeItem.description}</p>
            </div>

            <button
              onClick={handleWhatsApp}
              className="btn-burnt-peach px-6 py-3.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-2 shadow-md w-full sm:w-auto"
            >
              <MessageCircle className="w-4 h-4 text-white" />
              <span>Inquire Transformation</span>
            </button>
          </div>

        </div>

      </div>
    </section>
  );
}
